// src/app/features/offers-policies/store/offers-policies.effects.ts
//
// NgRx effects for the offers-policies feature.
//
// WHY a local _inFlight set (instead of reading state):
// Reducers run BEFORE effects for the same action. If we tried to dedup by
// reading `slice.loading` from the store, the reducer would have already set it
// to `true` — so every effect run would short-circuit and no HTTP call would
// ever fire. Instead:
//   • Cached slices       → cache TTL check (`isCacheValid`) skips a fresh slice
//   • Search effects      → `switchMap` auto-cancels stale calls
//   • Keyed per-ID data   → local `Set<string>` tracks truly in-flight keys

import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, EMPTY } from 'rxjs';
import { switchMap, map, catchError, withLatestFrom, finalize } from 'rxjs/operators';

import { apiErrorMessage, apiErrorStatus } from '@shared/utils/api-error.util';

import { OffersPoliciesApiService } from '../services/offers-policies-api.service';
import { isCacheValid } from './offers-policies.state';
import {
  // Policies
  PolicySearchActions,
  PolicyDetailActions,
  PolicySummaryActions,
  PolicyPaymentsActions,
  PolicyClaimsActions,
  // Installments
  InstallmentsByPolicyActions,
  // Invoices
  InvoiceSearchActions,
  InvoiceDetailActions,
} from './offers-policies.actions';
import {
  selectPolicyDetailsMap,
  selectPolicySummariesMap,
  selectPolicyPaymentsMap,
  selectPolicyClaimsMap,
  selectInstallmentsByPolicyMap,
  selectInvoiceDetailsMap,
} from './offers-policies.selectors';

/** Fallback text of invoice load failures when neither the API nor the interceptor gave one. */
const INVOICE_ERROR_FALLBACK = 'Ndodhi një gabim i papritur.';

@Injectable()
export class OffersPoliciesEffects {
  private readonly actions$ = inject(Actions);
  private readonly store    = inject(Store);
  private readonly api      = inject(OffersPoliciesApiService);

  /**
   * Local in-flight key set — dedups keyed per-id HTTP calls (per-policy,
   * per-invoice, etc.). Lives in effects only, never state.
   */
  private readonly _inFlight = new Set<string>();

  private readError(err: unknown): string {
    const e = err as { error?: { message?: string }; message?: string };
    return e?.error?.message ?? e?.message ?? 'Ndodhi një gabim i papritur.';
  }

  // ==========================================================================
  // ── POLICIES ──
  // ==========================================================================

  searchPolicies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PolicySearchActions.search),
      switchMap(({ request }) =>
        this.api.searchPolicies(request).pipe(
          map(data => PolicySearchActions.success({ data })),
          catchError(err => of(PolicySearchActions.failure({ error: this.readError(err) }))),
        ),
      ),
    )
  );

  loadPolicyDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PolicyDetailActions.load),
      withLatestFrom(this.store.select(selectPolicyDetailsMap)),
      switchMap(([{ policyId, forceReload }, map$]) => {
        const slice = map$[policyId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `policyDetail:${policyId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getPolicy(policyId).pipe(
          map(data => PolicyDetailActions.success({ policyId, data })),
          catchError(err => of(PolicyDetailActions.failure({ policyId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  loadPolicySummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PolicySummaryActions.load),
      withLatestFrom(this.store.select(selectPolicySummariesMap)),
      switchMap(([{ policyId, forceReload }, map$]) => {
        const slice = map$[policyId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `policySummary:${policyId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getPolicySummary(policyId).pipe(
          map(data => PolicySummaryActions.success({ policyId, data })),
          catchError(err => of(PolicySummaryActions.failure({ policyId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  loadPolicyPayments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PolicyPaymentsActions.load),
      withLatestFrom(this.store.select(selectPolicyPaymentsMap)),
      switchMap(([{ policyId, forceReload }, map$]) => {
        const slice = map$[policyId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `policyPayments:${policyId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getPolicyPayments(policyId).pipe(
          map(data => PolicyPaymentsActions.success({ policyId, data })),
          catchError(err => of(PolicyPaymentsActions.failure({ policyId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  loadPolicyClaims$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PolicyClaimsActions.load),
      withLatestFrom(this.store.select(selectPolicyClaimsMap)),
      switchMap(([{ policyId, forceReload }, map$]) => {
        const slice = map$[policyId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `policyClaims:${policyId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getPolicyClaims(policyId).pipe(
          map(data => PolicyClaimsActions.success({ policyId, data })),
          catchError(err => of(PolicyClaimsActions.failure({ policyId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ==========================================================================
  // ── INSTALLMENTS ──
  // ==========================================================================

  loadInstallmentsByPolicy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InstallmentsByPolicyActions.load),
      withLatestFrom(this.store.select(selectInstallmentsByPolicyMap)),
      switchMap(([{ policyId, forceReload }, map$]) => {
        const slice = map$[policyId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `installments:${policyId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getInstallments(policyId).pipe(
          map(data => InstallmentsByPolicyActions.success({ policyId, data })),
          catchError(err => of(InstallmentsByPolicyActions.failure({ policyId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ==========================================================================
  // ── INVOICES ──
  // ==========================================================================

  // Invoice errors go through apiErrorMessage: the API's own message, then
  // ModelState / ProblemDetails, then the interceptor's translated text — never
  // Angular's technical "Http failure response for …".
  searchInvoices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InvoiceSearchActions.search),
      switchMap(({ request }) =>
        this.api.searchInvoices(request).pipe(
          map(data => InvoiceSearchActions.success({ data })),
          catchError(err => of(InvoiceSearchActions.failure({ error: apiErrorMessage(err, INVOICE_ERROR_FALLBACK) }))),
        ),
      ),
    )
  );

  loadInvoiceDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InvoiceDetailActions.load),
      withLatestFrom(this.store.select(selectInvoiceDetailsMap)),
      switchMap(([{ invoiceId, forceReload }, map$]) => {
        const slice = map$[invoiceId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `invoiceDetail:${invoiceId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getInvoice(invoiceId).pipe(
          map(data => InvoiceDetailActions.success({ invoiceId, data })),
          catchError(err => of(InvoiceDetailActions.failure({
            invoiceId,
            error: apiErrorMessage(err, INVOICE_ERROR_FALLBACK),
            status: apiErrorStatus(err),
          }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );
}
